<template>
  <div
    class="h-fit min-w-[300px] rounded-md border shadow-sm"
    :data-cy="`offers-attributes-card-${offer.id}`"
  >
    <div class="flex flex-col rounded-md bg-white text-sm">
      <div
        class="flex flex-col space-y-4 divide-y pb-4 sm:w-full sm:items-start sm:space-y-0 sm:gap-y-4"
        data-cy="offers-basic-attributes"
      >
        <div class="flex w-full flex-wrap items-center justify-between p-4">
          <div
            class="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4"
          >
            <div class="flex space-x-2">
              <span class="content-center">
                <icon-base
                  width="30"
                  height="30"
                  view-box="0 0 40 40"
                  class="h-10"
                  :icon="IconArloc"
                />
              </span>
              <div class="flex items-center space-x-1">
                <div class="flex flex-col">
                  <span class="text-sm font-semibold">
                    {{ offer.product }}
                  </span>
                  <span class="text-xs font-normal text-label">
                    {{ offer.funder?.full_name }}
                  </span>
                </div>
                <icon-base
                  v-tooltip="{
                    content: offer.product_description,
                    onShow: () => !!offer.product_description,
                  }"
                  class="mt-0.5 text-gray-300"
                  height="16"
                  width="16"
                  view-box="0 0 22 22"
                  :icon="IconInfo"
                />
              </div>
            </div>
            <div class="flex space-x-4">
              <div
                v-if="offer.accepted"
                class="flex h-5 max-w-max min-w-max items-center rounded-full bg-emerald-400/10 pr-2 pl-0.5 text-[10px] font-medium whitespace-nowrap uppercase select-none"
              >
                <icon-base
                  class="mt-1 text-emerald-400"
                  height="18"
                  width="18"
                  view-box="0 0 20 20"
                  :icon="IconSuccessRound"
                />
                <span class="font-semibold text-emerald-400">
                  {{ $t("COMMON.ACCEPTED") }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex space-x-3">
            <template v-if="!offer.accepted">
              <lf-button
                class="w-full rounded-sm bg-red-100 p-4 text-sm leading-none font-medium text-red-500"
              >
                {{ $t("COMMON.DECLINE") }}
              </lf-button>
              <lf-button class="bg-primary/10 text-primary">
                {{ $t("COMMON.ACCEPT") }}
              </lf-button>
            </template>
            <lf-button
              v-if="expandedFields.length"
              size="small"
              class="h-10 w-full rounded-sm border border-gray-200 bg-white text-sm leading-none font-medium text-label"
              @click="showAdditionalAttributes = !showAdditionalAttributes"
            >
              <icon-base
                height="6"
                width="10"
                :icon="IconDropdownArrow"
                :class="{ 'rotate-180 transform': showAdditionalAttributes }"
              />
            </lf-button>
          </div>
        </div>
        <div
          v-if="primaryFields.length"
          class="flex flex-wrap justify-between gap-y-4 p-4 py-4 sm:w-full sm:gap-x-10 sm:py-0 sm:pt-4"
        >
          <div
            v-for="({ label, value, type, name }, index) in primaryFields"
            :key="label"
            :class="index % 2 ? 'items-end' : 'items-start'"
            :data-cy="`offers-basic-${label.replace(/ /g, '-')}`"
            class="flex w-1/2 flex-col text-xs sm:w-fit"
          >
            <span class="font-semibold text-headline">
              {{ labelForField(value, type, name) || "-" }}
            </span>
            <span class="max-w-48 font-normal text-label">
              {{ label }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showAdditionalAttributes"
      class="w-full rounded-sm bg-[#F9F9F9] p-4"
    >
      <div v-if="!!expandedFields.length" class="font-semibold">
        {{ $t("DEALS.DEAL_PROGRESS.OFFER_DETAILS") }}
      </div>
      <div
        v-if="!!expandedFields.length"
        class="relative pt-2"
        data-cy="offers-additional-attributes"
        :class="{
          'grid gap-x-10 gap-y-2 lg:grid-cols-1 xl:grid-cols-2':
            !!expandedFields.length,
        }"
      >
        <template v-if="expandedFields.length">
          <div
            v-for="(dividerClass, dividerNum) in dividers"
            :key="`divider-${dividerNum}-${offer.id}`"
            :class="dividerClass"
            class="divider"
          ></div>
        </template>

        <div
          v-for="{ label, value, type, name } in expandedFields"
          :key="`${offer.id}-additional-data-${label.replace(/ /g, '-')}`"
          :data-cy="`offers-additional-${label.replace(/ /g, '-')}`"
          class="flex justify-between text-xs"
        >
          <span class="max-w-48">
            {{ label }}
          </span>
          <span
            class="max-w-[9rem] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ labelForField(value, type, name) || "-" }}
          </span>
        </div>
      </div>
      <div
        v-if="note"
        class="mt-2 w-full flex-col space-y-3 text-sm"
        data-cy="offers-note-section"
      >
        <div class="flex items-center">
          <icon-offer-note class="text-primary" />
          <span
            class="font-medium text-gray-500"
            data-cy="offer-note-section-title"
          >
            {{ $t("COMMON.NOTE") }}
          </span>
          <icon-base
            class="mt-0.5 ml-2 text-gray-300"
            height="16"
            width="16"
            view-box="0 0 22 22"
            :icon="IconInfo"
          />
        </div>

        <p
          class="overflow-hidden text-start break-words text-ellipsis text-gray-500"
          data-cy="offer-note"
        >
          {{ note.label }}
        </p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, ref } from "vue";

import orderBy from "lodash/orderBy";

import { BpOfferSection } from "@/enums/offerTemplates";
import { FieldType } from "@/enums/offers";
import { formatMoney } from "@/helpers/formatting";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconInfo from "@/components/icons/IconInfo.vue";
import IconSuccessRound from "@/components/icons/IconSuccessRound.vue";
import IconArloc from "@/components/icons/fpProductTypes/IconArloc.vue";

import type { OfferView } from "@/models/offers";

const props = defineProps<{
  offer: OfferView;
}>();

const { greaterOrEqual } = useBreakpoints(breakpointsTailwind);
const [is2Xl, isLg] = (["2xl", "lg"] as const).map(greaterOrEqual);

const showAdditionalAttributes = ref(true);

const primaryFields = computed(() => {
  const details = props.offer.details.filter(
    (detail) =>
      detail.name !== "note" && detail.section === BpOfferSection.PRIMARY
  );

  return orderBy(details, "weight");
});

const expandedFields = computed(() => {
  const details = props.offer.details.filter(
    (detail) =>
      detail.name !== "note" && detail.section === BpOfferSection.EXPANDED
  );

  return orderBy(details, "weight");
});

const dividers = computed((): Record<number, string> => {
  if (isLg.value || is2Xl.value) {
    return { 1: "left-1/2" };
  }

  return {};
});

const note = computed(() =>
  props.offer.details.find((detail) => detail.name === "note")
);

const labelForField = (
  value: string | number,
  type: FieldType,
  fieldName: string
) => {
  if (type === FieldType.CURRENCY) {
    return formatMoney(value);
  }

  if (type === FieldType.OPTIONS) {
    const field = props.offer.details.find(({ name }) => name === fieldName);
    return field?.options?.[value] || "-";
  }

  return value;
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.divider {
  height: calc(100% - 0.5rem);
  @apply absolute top-2 w-px bg-disabled;
}
</style>
